// Tela 3 — Letra capitular. Extraído de entrevista-sergio-gargantini.html
// (bloco #s2). Ver DOCS/03-letra-capitular.md — o `shape-outside` foi medido
// pixel a pixel especificamente pra letra "D"; qualquer outra letra usa o
// fallback documentado (retângulo normal via só `float:left`).
import { escapeHtml, toParagraphs } from '../shared/text'
import { LOGO_PRETO_DATA_URI } from '../shared/logo'
import type { TemplateDefinition } from '../types'

const D_SHAPE_OUTSIDE =
  'polygon(100% 1%, 82% 3.5%, 89.7% 7%, 93.7% 10.5%, 96% 14%, 97.6% 18.7%, 98.1% 22.2%, 98.3% 29%, 98.5% 36%, 98.7% 49%, 98.7% 61%, 98.3% 72.5%, 97.4% 83%, 93.9% 90%, 83.3% 97%, 74.6% 99.4%, 0% 100%, 0% 0%)'

export const CSS = `
  .tpl-letra-capitular{
    display:flex;
    flex-direction:column;
    justify-content:center;
    padding:0 44px;
    position:relative;
  }
  .tpl-letra-capitular .dropcap-block::after{
    content:"";
    display:table;
    clear:both;
  }
  .tpl-letra-capitular .capital{
    float:left;
    font-family:'Anton',sans-serif;
    font-size:min(calc(var(--template-vw-69, 69vw) * var(--capital-scale, 1)), calc(450px * var(--capital-scale, 1)));
    line-height:.78;
    color:var(--ink);
    margin-right:3.5%;
    shape-margin:2%;
  }
  .tpl-letra-capitular .capital--d{
    shape-outside:${D_SHAPE_OUTSIDE};
  }
  .tpl-letra-capitular .body-copy{
    font-family:'Source Serif 4',serif;
    font-weight:300;
    font-size:clamp(13px,var(--template-vw-2-85, 2.85vw),17.5px);
    line-height:1.78;
    text-align:justify;
    color:var(--ink);
  }
  .tpl-letra-capitular .body-copy p + p{margin-top:1.4em;}
  .tpl-letra-capitular .logo{position:absolute; right:44px; bottom:40px; height:24px;}
`

function render(content: Record<string, unknown>): string {
  const rawLetter = String(content.capitalLetter ?? 'D').trim().charAt(0) || 'D'
  const letter = escapeHtml(rawLetter.toUpperCase())
  const isD = rawLetter.toUpperCase() === 'D'
  const capitalScale = Number(content.capitalScale ?? 1)
  const safeCapitalScale = Number.isFinite(capitalScale) ? Math.min(1.4, Math.max(0.7, capitalScale)) : 1
  const paragraphs = toParagraphs(String(content.body ?? ''))
    .map((p) => `<p>${escapeHtml(p)}</p>`)
    .join('')

  return `<section class="slide tpl-letra-capitular">
    <div class="dropcap-block">
      <div class="capital${isD ? ' capital--d' : ''} reveal r1" style="--capital-scale:${safeCapitalScale}">${letter}</div>
      <div class="body-copy reveal r2">${paragraphs}</div>
    </div>
    <div class="logo reveal r3"><img src="${LOGO_PRETO_DATA_URI}" alt="Lorena"></div>
  </section>`
}

export const letraCapitularTemplate: TemplateDefinition = {
  id: 'letra-capitular',
  label: 'Letra capitular',
  tags: ['homem', 'texto'],
  css: CSS,
  fieldsSchema: [
    {
      key: 'capitalLetter',
      label: 'Letra capitular',
      type: 'text',
      defaultValue: 'D',
      help: 'Com a letra "D" o texto contorna a curva dela; com outras letras, o texto fica alinhado reto ao lado.',
    },
    {
      key: 'capitalScale',
      label: 'Tamanho da capitular',
      type: 'range',
      min: 0.7,
      max: 1.4,
      step: 0.05,
      defaultValue: 1,
      help: 'Aumenta ou reduz a capitular; o texto corrido se reorganiza automaticamente ao redor dela.',
    },
    {
      key: 'body',
      label: 'Texto corrido',
      type: 'textarea',
      help: 'Deixe uma linha em branco entre parágrafos. A 1ª palavra deve continuar a letra acima (ex.: "D" + "a periferia..." = "Da periferia...").',
      placeholder: 'a periferia de exemplo ao desenvolvimento de um texto de exemplo...\n\nSegundo parágrafo de exemplo aqui.',
    },
  ],
  render,
  previewContent: {
    capitalLetter: 'D',
    capitalScale: 1,
    body: 'e exemplo aqui, mostrando como o texto contorna a letra capitular ao lado.\n\nSegundo parágrafo de exemplo, já em largura total.',
  },
}
